import type Phaser from "phaser";
import type { SimEvent, WorldState } from "../../sim";
import { loadSettings, type Settings, saveSettings } from "../settings";
import { EngineMixer, engineMix } from "./engine-mixer";
import { type Listener, soundsForEvents } from "./sound-policy";
import { VoicePool } from "./voice-pool";

/**
 * Plays match sounds from sim events through a fixed voice pool. Phaser's SoundManager
 * unlocks Web Audio on the first user gesture and pauses audio when the tab loses focus.
 * Sounds that weren't loaded (no Web Audio) are skipped silently.
 */
export class AudioPlayer {
	private readonly pool: VoicePool;
	private readonly engines: EngineMixer;
	private settings: Settings;

	constructor(
		private readonly scene: Phaser.Scene,
		private readonly listeners: readonly Listener[] = [],
	) {
		this.settings = loadSettings();
		this.pool = new VoicePool((key) =>
			scene.cache.audio.exists(key) ? scene.sound.add(key) : null,
		);
		this.engines = new EngineMixer(() =>
			scene.cache.audio.exists("engine") ? scene.sound.add("engine", { loop: true }) : null,
		);
		this.apply();
	}

	/** Every frame while the match runs: engine loops follow the ships' thrust (P11.9). */
	updateEngines(world: WorldState, deltaMs: number): void {
		this.engines.update(engineMix(world, this.listeners), deltaMs);
	}

	/** Paused, ended or a paused replay: engines stop. */
	silenceEngines(): void {
		this.engines.silence();
	}

	handleEvents(events: readonly SimEvent[], world: WorldState): void {
		for (const cue of soundsForEvents(events, world, this.listeners))
			this.pool.play(cue.key, cue.pan);
	}

	toggleMute(): void {
		this.settings = { ...this.settings, muted: !this.settings.muted };
		saveSettings(this.settings);
		this.apply();
	}

	get muted(): boolean {
		return this.settings.muted;
	}

	destroy(): void {
		this.pool.stopAll();
		this.engines.silence();
	}

	private apply(): void {
		this.scene.sound.volume = this.settings.volume;
		this.scene.sound.mute = this.settings.muted;
	}
}
